import {
  collection,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  writeBatch,
} from 'firebase/firestore';
import {
  COL,
  SUB,
  type Category,
  type OptionGroup,
  type OptionValue,
  type PriceTier,
  type Product,
  type ProductShipping,
} from '@eprintsmart/shared';
import { db } from '../firebase';
import { notifyProductChanged } from './indexnow';

/* ------------------------------------------------------------------ *
 * Categories
 * ------------------------------------------------------------------ */

export function subscribeToCategories(onChange: (categories: Category[]) => void) {
  return onSnapshot(query(collection(db, COL.categories), orderBy('sortOrder')), (snap) => {
    onChange(snap.docs.map((d) => ({ ...(d.data() as Category), id: d.id })));
  });
}

export interface CategoryNode extends Category {
  children: CategoryNode[];
  /** Resolved here so a row can spot a child repeating its parent's name. */
  parentName: string | null;
}

/** Rebuilds the tree from the flat collection, sorted at every level. */
export function buildCategoryTree(categories: Category[]): CategoryNode[] {
  const byId = new Map<string, CategoryNode>();
  for (const c of categories) byId.set(c.id, { ...c, children: [], parentName: null });

  const roots: CategoryNode[] = [];
  for (const node of byId.values()) {
    node.parentName = node.parentId ? (byId.get(node.parentId)?.name ?? null) : null;
    if (node.parentId) {
      // A category whose parent was deleted would otherwise vanish from the
      // tree entirely; surfacing it at the root makes the orphan fixable.
      const parent = byId.get(node.parentId);
      if (parent) parent.children.push(node);
      else roots.push(node);
    } else {
      roots.push(node);
    }
  }

  const sortRec = (nodes: CategoryNode[]) => {
    nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);

  return roots;
}

export async function saveCategory(id: string, patch: Partial<Category>) {
  await setDoc(doc(db, COL.categories, id), { ...patch, id, updatedAt: Date.now() }, { merge: true });
}

/**
 * Deleting a category that still has children or products would leave both
 * unreachable, so the caller must resolve those first.
 */
export async function deleteCategory(id: string, allCategories: Category[]) {
  const hasChildren = allCategories.some((c) => c.parentId === id);
  if (hasChildren) throw new Error('Move or delete the sub-categories first.');

  const products = await getDocs(collection(db, COL.products));
  const used = products.docs.filter((d) => (d.data().categoryIds ?? []).includes(id));
  if (used.length) {
    throw new Error(
      `${used.length} product${used.length === 1 ? '' : 's'} still use this category.`,
    );
  }

  await deleteDoc(doc(db, COL.categories, id));
}

export async function reorderCategories(ordered: { id: string; sortOrder: number }[]) {
  const batch = writeBatch(db);
  for (const { id, sortOrder } of ordered) {
    batch.update(doc(db, COL.categories, id), { sortOrder, updatedAt: Date.now() });
  }
  await batch.commit();
}

/* ------------------------------------------------------------------ *
 * Products
 * ------------------------------------------------------------------ */

export function subscribeToProducts(onChange: (products: Product[]) => void) {
  return onSnapshot(query(collection(db, COL.products), orderBy('name')), (snap) => {
    onChange(snap.docs.map((d) => ({ ...(d.data() as Product), id: d.id })));
  });
}

export interface HydratedProduct {
  product: Product;
  optionGroups: (OptionGroup & { values: OptionValue[] })[];
  priceTiers: PriceTier[];
}

/** Loads a product with its option groups, values and price ladder. */
export async function loadProduct(id: string): Promise<HydratedProduct | null> {
  const snap = await getDoc(doc(db, COL.products, id));
  if (!snap.exists()) return null;

  const groupsSnap = await getDocs(
    query(collection(db, `${COL.products}/${id}/${SUB.optionGroups}`), orderBy('sortOrder')),
  );

  const optionGroups = await Promise.all(
    groupsSnap.docs.map(async (g) => {
      const valuesSnap = await getDocs(
        query(
          collection(db, `${COL.products}/${id}/${SUB.optionGroups}/${g.id}/${SUB.optionValues}`),
          orderBy('sortOrder'),
        ),
      );
      return {
        ...(g.data() as OptionGroup),
        id: g.id,
        values: valuesSnap.docs.map((v) => ({ ...(v.data() as OptionValue), id: v.id })),
      };
    }),
  );

  const tiersSnap = await getDocs(
    query(collection(db, `${COL.products}/${id}/${SUB.priceTiers}`), orderBy('qty')),
  );

  return {
    product: { ...(snap.data() as Product), id: snap.id },
    optionGroups,
    priceTiers: tiersSnap.docs.map((t) => ({ ...(t.data() as PriceTier), id: t.id })),
  };
}

/** Creates a product with a sane default print spec so the editor opens on
 * something valid rather than a form full of zeroes. */
export async function createProduct(input: {
  slug: string;
  name: string;
  categoryIds: string[];
}) {
  const existing = await getDoc(doc(db, COL.products, input.slug));
  if (existing.exists()) throw new Error(`A product with the slug "${input.slug}" already exists.`);

  const now = Date.now();
  await setDoc(doc(db, COL.products, input.slug), {
    id: input.slug,
    ...input,
    shortDescription: '',
    description: '',
    highlights: [],
    imageIds: [],
    printSpec: {
      trimWidthMm: 89, trimHeightMm: 51, bleedMm: 3, safeMarginMm: 3,
      colorMode: 'CMYK', minDpi: 300, sides: 1,
    },
    basePriceMinor: 0,
    minQty: 1,
    allowCustomQty: false,
    turnaroundDays: 3,
    gstRatePercent: 18,
    hsnCode: '4911',
    // Starts as a draft: a product with no price ladder and no images has no
    // business being on the storefront.
    status: 'DRAFT',
    createdAt: now,
    updatedAt: now,
  });
  return input.slug;
}

/**
 * Every product edit funnels through here, including archive and restore, which
 * is why the search-engine ping hangs off this one function rather than being
 * repeated at each call site. It is fired after the write lands and never
 * awaited — see notifyProductChanged.
 *
 * createProduct is deliberately NOT pinged: a new product starts as a DRAFT and
 * has no public page yet, so announcing it would send the engines to a 404.
 */
export async function saveProduct(id: string, patch: Partial<Product>) {
  await setDoc(doc(db, COL.products, id), { ...patch, id, updatedAt: Date.now() }, { merge: true });
  notifyProductChanged(id);
}

/**
 * Saves the print spec as a WHOLE map, so a field can be removed.
 *
 * saveProduct writes with setDoc + merge:true, which deep-merges nested maps —
 * dropping a key from the spec object leaves the old value untouched in
 * Firestore. So "set this product's mockup back to none" silently did nothing.
 * updateDoc replaces a map value outright, which is what removal needs.
 *
 * Undefined values are stripped first: the client SDK rejects them outright
 * (ignoreUndefinedProperties is deliberately not set on this instance), so a
 * cleared field would throw rather than save.
 */
export async function saveProductPrintSpec(id: string, spec: Record<string, unknown>) {
  const clean = Object.fromEntries(Object.entries(spec).filter(([, v]) => v !== undefined));
  await updateDoc(doc(db, COL.products, id), { printSpec: clean, updatedAt: Date.now() });
  notifyProductChanged(id);
}

/**
 * Saves the shipping weight as a WHOLE map, for the same reason as the print
 * spec: switching a product from GSM to grams-per-piece must remove the old
 * key, and a merge would leave it behind - and gsm wins over a fixed weight.
 */
export async function saveProductShipping(id: string, shipping: ProductShipping) {
  const clean = Object.fromEntries(Object.entries(shipping).filter(([, v]) => v !== undefined));
  await updateDoc(doc(db, COL.products, id), { shipping: clean, updatedAt: Date.now() });
  notifyProductChanged(id);
}

export async function setProductImages(id: string, imageIds: string[]) {
  await updateDoc(doc(db, COL.products, id), { imageIds, updatedAt: Date.now() });
  notifyProductChanged(id);
}

/* ------------------------------------------------------------------ *
 * Price tiers
 * ------------------------------------------------------------------ */

export async function savePriceTiers(productId: string, tiers: PriceTier[]) {
  const path = `${COL.products}/${productId}/${SUB.priceTiers}`;
  const existing = await getDocs(collection(db, path));

  const batch = writeBatch(db);
  const keep = new Set(tiers.map((t) => t.id));

  for (const stale of existing.docs) {
    if (!keep.has(stale.id)) batch.delete(stale.ref);
  }
  for (const tier of tiers) {
    batch.set(doc(db, path, tier.id), tier, { merge: true });
  }

  await batch.commit();
  // A ladder change rewrites both the product page and the /pricing table, and
  // a stale price quoted back by an assistant is exactly the failure this whole
  // effort exists to avoid.
  notifyProductChanged(productId);
}

/**
 * Flags ladders that would misprice or look wrong on the storefront. Returned
 * rather than thrown so the editor can show them inline without blocking a
 * half-finished edit.
 */
export function validateTierLadder(tiers: PriceTier[]): string[] {
  const problems: string[] = [];
  const byKey = new Map<string, PriceTier[]>();

  for (const tier of tiers) {
    if (!byKey.has(tier.optionKey)) byKey.set(tier.optionKey, []);
    byKey.get(tier.optionKey)!.push(tier);
  }

  for (const [key, ladder] of byKey) {
    const label = key === '*' ? 'Default ladder' : `Ladder "${key}"`;
    const sorted = [...ladder].sort((a, b) => a.qty - b.qty);

    const seen = new Set<number>();
    for (const tier of sorted) {
      if (seen.has(tier.qty)) problems.push(`${label}: duplicate quantity ${tier.qty}.`);
      seen.add(tier.qty);
      if (tier.qty <= 0) problems.push(`${label}: quantity must be positive.`);
      if (tier.priceMinor <= 0) problems.push(`${label}: ${tier.qty} has no price.`);
    }

    // The badge on the storefront reads "Save X%" against the smallest tier —
    // a rising per-unit rate makes that negative and the ladder nonsensical.
    for (let i = 1; i < sorted.length; i += 1) {
      const prev = sorted[i - 1]!;
      const curr = sorted[i]!;
      const prevRate = prev.priceMinor / prev.qty;
      const currRate = curr.priceMinor / curr.qty;
      if (currRate > prevRate) {
        problems.push(
          `${label}: ${curr.qty} costs more per unit than ${prev.qty} — buying more should never cost more each.`,
        );
      }
    }
  }

  return problems;
}

/* ------------------------------------------------------------------ *
 * Products within a category
 * ------------------------------------------------------------------ */

/**
 * Products grouped by the categories they are DIRECTLY attached to.
 *
 * Deliberately not the storefront's descendant rollup
 * (storefront/src/lib/catalog.ts getProductsForCategory), which BFS-expands a
 * node and matches any descendant. That is right for a shopper — browsing a
 * group should show everything beneath it — and wrong here, where it would list
 * the same product under the group, the category AND the variant, and make
 * every count wrong.
 *
 * Pure, so the tree can memoise it once. Filtering inside the recursive row
 * component would be O(categories x products) on every render.
 *
 * Note products are not confined to leaves: NewProductDialog offers every
 * category with level > 0, so a level-1 node can hold products directly.
 */
export function indexProductsByCategory(
  products: Product[],
  categories: Category[],
): Map<string, Product[]> {
  // Products denormalise their whole ancestor chain into categoryIds: 226 of
  // 241 carry leaf + parent + grandparent. Indexing every id would file one
  // product under three nodes, so the tree shows it three times and every
  // count triples. Each product belongs at its DEEPEST category and nowhere
  // else; the ancestors are reachable by collapsing to it.
  //
  // Depth is computed from parentId rather than trusting the stored `level`,
  // which is denormalised too and can drift after a re-parent.
  const byId = new Map(categories.map((c) => [c.id, c]));
  const depthOf = (id: string): number => {
    let depth = 0;
    let cursor = byId.get(id);
    // Bounded: a parentId cycle would otherwise spin forever.
    while (cursor?.parentId && depth < 16) {
      cursor = byId.get(cursor.parentId);
      depth += 1;
    }
    return depth;
  };

  const byCategory = new Map<string, Product[]>();
  for (const product of products) {
    const ids = (product.categoryIds ?? []).filter((id) => byId.has(id));
    if (ids.length === 0) continue;

    let deepest = ids[0];
    let best = depthOf(deepest);
    for (const id of ids.slice(1)) {
      const d = depthOf(id);
      if (d > best) {
        best = d;
        deepest = id;
      }
    }

    const list = byCategory.get(deepest);
    if (list) list.push(product);
    else byCategory.set(deepest, [product]);
  }
  return byCategory;
}

/**
 * Products whose categoryIds are all dangling, or absent entirely. They are
 * invisible in the tree, so the page has to say they exist or they are lost.
 */
export function productsWithoutCategory(
  products: Product[],
  categories: Category[],
): Product[] {
  const ids = new Set(categories.map((c) => c.id));
  return products.filter(
    // `unlisted` products have no category on purpose — the print-lab sheet is
    // ordered by code, not browsed. Warning about them trains you to ignore the
    // warning, which is worse than not having it.
    (p) => !p.unlisted && !(p.categoryIds ?? []).some((id) => ids.has(id)),
  );
}

/** Deliberately outside the tree — listed for visibility, not as a problem. */
export function unlistedProducts(products: Product[]): Product[] {
  return products.filter((p) => p.unlisted);
}

/**
 * Takes a product off the storefront without destroying it.
 *
 * A hard delete would orphan the priceTiers and optionGroups subcollections
 * (the web SDK cannot recursive-delete) and leave past orders' reports reading
 * back through a product that no longer exists. Archiving is already understood
 * end to end: the storefront queries status == 'ACTIVE', and orders snapshot
 * the product at purchase, so history is unaffected.
 */
export async function archiveProduct(id: string) {
  await saveProduct(id, { status: 'ARCHIVED' });
}

/**
 * Brings an archived product back as a DRAFT, not straight to ACTIVE.
 *
 * Whatever caused it to be archived — wrong price, missing images — is probably
 * still true, and republishing it unseen puts that back in front of customers.
 * DRAFT makes someone look before it sells again.
 */
export async function restoreProduct(id: string) {
  await saveProduct(id, { status: 'DRAFT' });
}
